import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createSupplyAppsClient } from './api';
import { supplyAppStatusCountsFilter } from './statusCounts';
import type {
  AppRequest,
  AppRequestsListFilter,
  CreateAppFromRequestPayload,
  PublisherAppImportPayload,
  PublisherAppValidationPayload,
  SupplyAppMutationPayload,
  SupplyAppsListFilter,
} from './model';

export const supplyAppsQueryKey = ['supply', 'apps'] as const;
export const appRequestsQueryKey = ['supply', 'app-requests'] as const;
export const supplyAppsListsQueryKey = [...supplyAppsQueryKey, 'list'] as const;
export const supplyAppStatusCountsQueryKey = [...supplyAppsQueryKey, 'status-counts'] as const;
export const appRequestsListsQueryKey = [...appRequestsQueryKey, 'list'] as const;

function useSupplyAppsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return createSupplyAppsClient({
    config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
    accessToken: session.accessToken,
  });
}

export function useSupplyAppsQuery(filter: SupplyAppsListFilter, enabled = true) {
  const client = useSupplyAppsClient();
  return useQuery({
    queryKey: [...supplyAppsListsQueryKey, filter],
    queryFn: () => client.listApps(filter),
    enabled,
  });
}

export function useSupplyAppStatusCountsQuery(filter: SupplyAppsListFilter) {
  const client = useSupplyAppsClient();
  const countFilter = supplyAppStatusCountsFilter(filter);
  return useQuery({
    queryKey: [...supplyAppStatusCountsQueryKey, countFilter],
    queryFn: () => client.countAppsByStatus(countFilter),
  });
}

export function useSupplyAppQuery(id: string | undefined) {
  const client = useSupplyAppsClient();
  return useQuery({
    queryKey: [...supplyAppsQueryKey, 'detail', id],
    queryFn: () => client.getApp(id ?? ''),
    enabled: Boolean(id),
    refetchOnMount: 'always',
  });
}

export function useAppRequestsQuery(filter: AppRequestsListFilter) {
  const client = useSupplyAppsClient();
  return useQuery({
    queryKey: [...appRequestsListsQueryKey, filter],
    queryFn: () => client.listAppRequests(filter),
  });
}

export function useSupplyAppPublisherSearch(searchTerm: string, enabled = true) {
  const client = useSupplyAppsClient();
  return useQuery({
    queryKey: [...supplyAppsQueryKey, 'publisher-search', searchTerm],
    queryFn: () => client.searchPublishers(searchTerm),
    enabled: enabled && Boolean(searchTerm.trim()),
  });
}

export function useSupplyAppsReferences({
  publishersEnabled = true,
}: { publishersEnabled?: boolean } = {}) {
  const client = useSupplyAppsClient();
  const platforms = useQuery({
    queryKey: [...supplyAppsQueryKey, 'platforms'],
    queryFn: client.listPlatforms,
  });
  const iabCategories = useQuery({
    queryKey: [...supplyAppsQueryKey, 'iab-categories'],
    queryFn: client.listIabCategories,
  });
  const publishers = useQuery({
    queryKey: [...supplyAppsQueryKey, 'publishers'],
    queryFn: client.listPublishers,
    enabled: publishersEnabled,
  });
  return { platforms, iabCategories, publishers };
}

export function useCollectedAppDataQuery(
  publisherId: string | undefined,
  appStoreUrl: string | undefined,
) {
  const client = useSupplyAppsClient();
  return useQuery({
    queryKey: [...appRequestsQueryKey, 'url-data', publisherId, appStoreUrl],
    queryFn: () => client.collectAppFromUrl(publisherId ?? '', appStoreUrl ?? ''),
    enabled: Boolean(publisherId && appStoreUrl),
    retry: false,
  });
}

export function useSupplyAppsMutations() {
  const client = useSupplyAppsClient();
  const queryClient = useQueryClient();
  const invalidateApps = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: supplyAppsListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: supplyAppStatusCountsQueryKey }),
    ]);
  const invalidateRequests = () =>
    queryClient.invalidateQueries({ queryKey: appRequestsListsQueryKey });
  return {
    update: useMutation({
      mutationFn: ({ id, payload }: { id: string; payload: SupplyAppMutationPayload }) =>
        client.updateApp(id, payload),
      onSuccess: invalidateApps,
    }),
    archive: useMutation({ mutationFn: client.archiveApp, onSuccess: invalidateApps }),
    activate: useMutation({ mutationFn: client.activateApp, onSuccess: invalidateApps }),
    import: useMutation({ mutationFn: client.importApps, onSuccess: invalidateApps }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- read-only CSV export, writes no server state
    exportApps: useMutation({ mutationFn: client.exportApps }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- preview only, collects app data without saving
    previewPublisherAppsFromUrls: useMutation({
      mutationFn: ({ publisherId, urls }: { publisherId: string; urls: string[] }) =>
        client.collectPublisherAppsFromUrls(publisherId, urls),
    }),
    previewPublisherAppsManually: useMutation({
      mutationFn: ({
        publisherId,
        apps,
      }: {
        publisherId: string;
        apps: PublisherAppValidationPayload[];
      }) => client.validatePublisherApps(publisherId, apps),
    }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- preview only, collects app data without saving
    previewPublisherAppsFromCsv: useMutation({
      mutationFn: ({ publisherId, file }: { publisherId: string; file: File }) =>
        client.collectPublisherAppsFromCsv(publisherId, file),
    }),
    createPublisherApps: useMutation({
      mutationFn: ({
        publisherId,
        apps,
      }: {
        publisherId: string;
        apps: PublisherAppImportPayload[];
      }) => client.createPublisherApps(publisherId, apps),
      onSuccess: invalidateApps,
    }),
    downloadPublisherAppsCsvTemplate: useMutation({
      mutationFn: (publisherId: string) => client.downloadPublisherAppsCsvTemplate(publisherId),
    }),
    exportPublisherApps: useMutation({
      mutationFn: (publisherId: string) => client.exportPublisherApps(publisherId),
    }),
    reimportPublisherApps: useMutation({
      mutationFn: ({ publisherId, file }: { publisherId: string; file: File }) =>
        client.reimportPublisherApps(publisherId, file),
      onSuccess: invalidateApps,
    }),
    processRequests: useMutation({
      mutationFn: (requests: AppRequest[]) => client.processAppRequests(requests),
      onSuccess: () => {
        void invalidateRequests();
        void invalidateApps();
      },
    }),
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- read-only CSV export, writes no server state
    exportRequests: useMutation({ mutationFn: client.exportAppRequests }),
    createFromRequest: useMutation({
      mutationFn: async ({
        publisherId,
        payload,
        request,
      }: {
        publisherId: string;
        payload: CreateAppFromRequestPayload;
        request: AppRequest;
      }) => {
        const app = await client.createPublisherApp(publisherId, payload);
        return client.processAppRequests([{ ...request, appId: app.id, processed: true }]);
      },
      onSuccess: () => {
        void invalidateRequests();
        void invalidateApps();
      },
    }),
  };
}
